/**
 * Client-side "Recently Viewed reservations" store (Flow 7).
 *
 * Pure localStorage — zero backend. Scoped per tenant + property so switching
 * property does not leak another property's list. Cap of 10, most-recent-first,
 * deduped by booking id. All access is wrapped in try/catch because
 * localStorage can throw (private mode, quota, disabled storage).
 */

export interface RecentlyViewedEntry {
  id: string;
  guestName: string;
  unitName: string;
  checkIn: string;
  checkOut: string;
  /** epoch ms */
  viewedAt: number;
}

const CAP = 10;

/** Build the storage key. Returns null when scope is incomplete. */
export function recentlyViewedKey(
  tenantId: string | null | undefined,
  propertyId: string | null | undefined,
): string | null {
  if (!tenantId || !propertyId) return null;
  return `pms.recentlyViewed.${tenantId}.${propertyId}`;
}

/** Read the list (most-recent-first). Returns [] on any error/empty. */
export function getRecentlyViewed(key: string | null): RecentlyViewedEntry[] {
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is RecentlyViewedEntry =>
        !!e && typeof e === 'object' && typeof (e as RecentlyViewedEntry).id === 'string',
    );
  } catch {
    return [];
  }
}

/** Push an entry: dedupe by id, unshift, slice to CAP. */
export function pushRecentlyViewed(key: string | null, entry: RecentlyViewedEntry): void {
  if (!key) return;
  try {
    const list = getRecentlyViewed(key).filter((e) => e.id !== entry.id);
    list.unshift(entry);
    localStorage.setItem(key, JSON.stringify(list.slice(0, CAP)));
  } catch {
    /* localStorage unavailable — silently skip */
  }
}

/** Remove a single entry by id (prune-on-click for stale bookings). */
export function removeRecentlyViewed(key: string | null, id: string): void {
  if (!key) return;
  try {
    const list = getRecentlyViewed(key).filter((e) => e.id !== id);
    localStorage.setItem(key, JSON.stringify(list));
  } catch {
    /* ignore */
  }
}

/** Clear the whole list for this scope. */
export function clearRecentlyViewed(key: string | null): void {
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}
